<template lang="pug">
  .b-supplier-offer-form(data-testid="supplier-offer-form")
    ._title {{ title }}
    ._warning(v-if="sent" data-testid="supplier-offer-form-warning") Изменения увидит дизайнер
    ._grid
      ._field
        ._label Количество
        BaseInput(inputTestId="supplier-offer-form-quantity" v-model="quantity" :error="quantityError")
      ._field
        ._label Цена поставщика
        BaseInput(inputTestId="supplier-offer-form-price" v-model="supplierPrice" :error="priceError")
      ._field
        ._label Стоимость
        ._total(data-testid="supplier-offer-form-total") {{ money(total, '—') }}
      ._field
        ._label Персональная скидка
        ._row
          select._select(data-testid="supplier-offer-form-discount-type" v-model="discountType")
            option(
              v-for="(title, code) in DISCOUNT_TYPES"
              :key="code"
              :value="code"
              data-testid="supplier-offer-form-discount-type-option"
              ) {{ title }}
          BaseInput._value(inputTestId="supplier-offer-form-discount-value" v-model="discountValue")
      ._field
        ._label Срок поставки
        button._delivery-button(type="button" data-testid="supplier-offer-form-delivery" @click="editorOpened = true")
          | {{ deliveryText(deliveryTerm, 'указать срок') }}
        SpecDeliveryEditor(
          v-if="editorOpened"
          :value="deliveryTerm"
          @save="saveDelivery"
          @close="editorOpened = false"
          )
      ._field
        ._label
          label._replacement
            input._check(type="checkbox" data-testid="supplier-offer-form-replacement" v-model="isReplacement")
            | замена
        BaseInput(
          v-if="isReplacement"
          inputTestId="supplier-offer-form-replacement-text"
          v-model="replacementText"
          label="что предлагаете вместо запрошенного"
          )
      ._field.wide
        ._label Комментарий
        BaseTextarea(inputTestId="supplier-offer-form-comment" v-model="comment")
      ._field.wide(v-if="sent")
        ._label Причина изменения (необязательно)
        BaseInput(inputTestId="supplier-offer-form-reason" v-model="reason")
    ._buttons
      Btn(blue sm data-testid="supplier-offer-form-save" @click="save") Сохранить
      Btn(grey sm data-testid="supplier-offer-form-cancel" @click="$emit('cancel')") Отмена
</template>

<script>
import BaseInput from '@/components/BaseInput.vue';
import BaseTextarea from '@/components/BaseTextarea.vue';
import SpecDeliveryEditor from '@/components/specification/SpecDeliveryEditor.vue';
import {
  DISCOUNT_TYPES, deliveryText, money, round, toNumber,
} from '@/utils/spec';

/**
 * Форма отклика поставщика (ТЗ 6.5): количество (по умолчанию запрошенное) со стоимостью
 * «количество × цена», цена, тип и значение персональной скидки, срок общим
 * редактором структуры 2.4, признак замены с текстом, комментарий. Для отправленного отклика — та
 * же форма плюс необязательная причина изменения и предупреждение «изменения увидит дизайнер»
 * (ТЗ 4.2).
 *
 * Форма ничего не сохраняет сама: отдаёт поля наружу — так один и тот же компонент обслуживает
 * создание черновика и правку отправленного отклика. Сгенерировано Claude.
 */
export default {
  name: 'SupplierOfferForm',
  components: { BaseInput, BaseTextarea, SpecDeliveryEditor },
  props: {
    /** Отклик при правке; пусто при создании черновика. */
    offer: Object,
    /** Правится отправленный отклик: появляются причина изменения и предупреждение (ТЗ 6.5). */
    sent: Boolean,
    /** Запрошенное количество (кол-во план позиции) — значение по умолчанию для нового отклика. */
    requestedQuantity: [String, Number],
  },
  data() {
    const offer = this.offer ?? {};
    return {
      DISCOUNT_TYPES,
      quantity: String(toNumber(offer.quantity ?? this.requestedQuantity) ?? ''),
      quantityError: '',
      supplierPrice: offer.supplierPrice ?? '',
      discountType: offer.discountType ?? 'percent',
      discountValue: offer.discountValue ?? '0',
      deliveryTerm: offer.deliveryTerm ?? null,
      isReplacement: !!offer.isReplacement,
      replacementText: offer.replacementText ?? '',
      comment: offer.comment ?? '',
      reason: '',
      editorOpened: false,
      priceError: '',
    };
  },
  computed: {
    title() {
      if (this.sent) return 'Изменение отправленного отклика';
      return this.offer ? 'Черновик отклика' : 'Отклик вручную';
    },
    total() {
      const quantity = toNumber(this.quantity);
      const price = toNumber(this.supplierPrice);
      return quantity === null || price === null ? null : round(quantity * price, 2);
    },
  },
  methods: {
    deliveryText,
    money,
    saveDelivery(term) {
      this.deliveryTerm = term;
      this.editorOpened = false;
    },
    save() {
      const quantity = toNumber(this.quantity);
      this.quantityError = quantity === null || quantity <= 0 ? 'Нужно количество больше нуля' : '';
      const price = toNumber(this.supplierPrice);
      this.priceError = price === null || price <= 0 ? 'Нужна цена поставщика' : '';
      if (this.quantityError || this.priceError) return;

      const params = {
        quantity: String(quantity),
        supplierPrice: String(price),
        discountType: this.discountType,
        discountValue: String(toNumber(this.discountValue) ?? 0),
        deliveryTerm: this.deliveryTerm,
        isReplacement: this.isReplacement,
        replacementText: this.isReplacement ? (this.replacementText || null) : null,
        comment: this.comment || null,
      };
      if (this.sent && this.reason.trim()) params.reason = this.reason.trim();

      this.$emit('save', params);
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-offer-form {
  margin: 8px 0;
  padding: 12px;
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  background: var(--color-white);

  &__title {
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
  }

  &__warning {
    font-size: 12px;
    color: var(--color-orange);
    margin-bottom: 8px;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__field {
    flex: 0 0 200px;
    // всплывающий редактор срока позиционируется относительно своего поля (ТЗ 2.4)
    position: relative;

    &.wide {
      flex-basis: 100%;
    }
  }

  &__label {
    display: block;
    font-size: 12px;
    color: var(--color-middle-gray);
    margin-bottom: 4px;
  }

  &__row {
    display: flex;
    gap: 6px;
  }

  &__select {
    box-sizing: border-box;
    padding: 6px 8px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
  }

  &__value {
    flex-grow: 1;
  }

  &__total {
    padding: 8px 0;
    font-size: 13px;
    font-weight: 700;
  }

  &__delivery-button {
    box-sizing: border-box;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    background: var(--color-white);
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }

  &__replacement {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }

  &__check {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  &__buttons {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }
}
</style>
